import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useMatch } from 'react-router-dom';
import {
    ButtonProps,
    FormControl,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Select,
    SelectChangeEvent,
    Skeleton,
    styled,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    DataGridPremium,
    GridColDef,
    GridCsvExportOptions,
    GridExportMenuItemProps,
    GridPaginationModel,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarContainerProps,
    GridToolbarExportContainer,
    GridToolbarFilterButton,
    useGridApiContext,
} from '@mui/x-data-grid-premium';

import { Order } from '../../interfaces/services/orders';
import { privateRoutes } from '../../routes/appRouteList';
import { buildAreaLink, buildShipperLink } from '../../utils/navigationHelpers';
import { apiDateToLocalDate } from '../../utils/dateUtils';
import { formatNumber, formatCurrency, formatNumberPercent } from '../../utils/formattingUtils';
import { EquipmentTypeCode, TmsSources, ValueReferences } from '../../utils/enums';
import { useFilters } from '../Filters/useFilters';
import areas from '../../assets/data/areas.json';

const classesPrefix = 'orderTable';

const classes = {
    root: `${classesPrefix}-root`,
    orderCountBadge: `${classesPrefix}-orderCountBadge`,
    tableContents: `${classesPrefix}-tableContents`,
};

const StyledDiv = styled('div')(({ theme }) => {
    return {
        [`&.${classes.root}`]: {
            padding: theme.spacing(2),
            position: 'sticky',
            flexDirection: 'column',
            display: 'flex',
            top: 0,
            zIndex: 10,
            overflowY: 'hidden',
            maxHeight: '500px',
        },
        [`& .${classes.orderCountBadge}`]: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.getContrastText(theme.palette.primary.main),
            borderRadius: 10,
            height: 20,
            fontSize: '0.75em',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0px 6px',
            transform: 'translateY(-50%)',
        },
        [`& .${classes.tableContents}`]: {
            marginTop: theme.spacing(1),
            minHeight: 300,
            maxHeight: 500,
            flexBasis: 300,
            '& .MuiDataGrid-root .MuiDataGrid-cell:focus': {
                outline: 0,
            },
            '& .MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
                outline: 0,
            },
            '& .MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within': {
                outline: 0,
            },
            '& .MuiDataGrid-root .MuiDataGrid-toolbarContainer': {
                justifyContent: 'right',
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
            },
        },
    };
});

const ExportCsvMenuItem = ({
    options,
    hideMenu,
}: GridExportMenuItemProps<{
    label: string;
    csvOptions: GridCsvExportOptions;
}>): React.JSX.Element => {
    const apiRef = useGridApiContext();

    const handleExport = (): void => {
        apiRef.current.exportDataAsCsv({
            ...options?.csvOptions,
            fileName: 'Network Insights - Orders',
        });
    };

    return (
        <MenuItem
            onClick={(): void => {
                handleExport();

                // Hide the export menu after the export
                hideMenu?.();
            }}
        >
            {options?.label}
        </MenuItem>
    );
};

const CustomExportButton = (props: ButtonProps): React.JSX.Element => {
    return (
        <GridToolbarExportContainer {...props}>
            <ExportCsvMenuItem options={{ label: 'CSV (Visible Columns)', csvOptions: { allColumns: false } }} />
            <ExportCsvMenuItem options={{ label: 'CSV (All Columns)', csvOptions: { allColumns: true } }} />
        </GridToolbarExportContainer>
    );
};

const CustomToolbar = (props: GridToolbarContainerProps): React.JSX.Element => {
    return (
        <GridToolbarContainer {...props}>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <CustomExportButton />
        </GridToolbarContainer>
    );
};

const OrderTable = ({
    isLoading,
    orders,
    orderCount,
    equipmentType,
    handleEquipmentTypeChange,
}: {
    isLoading: boolean;
    orders: Order[];
    orderCount: number;
    equipmentType: EquipmentTypeCode;
    handleEquipmentTypeChange: (newEquipmentType: EquipmentTypeCode) => void;
}): React.JSX.Element => {
    const areaMatch = useMatch(privateRoutes.areas);
    const shipperMatch = useMatch(privateRoutes.shipperDetails);
    const filters = useFilters();

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);

    const getAreaValue = ({
        cityName,
        stateCode,
        mriAreaCode,
    }: {
        cityName: string | null;
        stateCode: string | null;
        mriAreaCode: string;
    }): string => {
        let displayValue = '';
        if (cityName && stateCode) {
            displayValue = `${cityName.trim()}, ${stateCode.trim().toUpperCase()}`;
        } else {
            const pickupArea = areas.find((area) => {
                return area.area_id === mriAreaCode;
            });

            displayValue = pickupArea?.area_name || mriAreaCode;
        }

        return displayValue;
    };

    const columns = useMemo((): GridColDef<Order>[] => {
        return [
            {
                headerName: 'Order Number',
                field: 'freight_order_number',
                flex: 1,
                minWidth: 165,
            },
            {
                headerName: 'Business Unit',
                field: 'carrier_business_unit_name',
                flex: 1,
                minWidth: 165,
            },
            {
                headerName: 'Shipper',
                field: 'pickup_party_name',
                renderCell: ({ row }): React.JSX.Element => {
                    if (shipperMatch?.params.id === row.pickup_party_id) {
                        return (
                            <Tooltip title={row.pickup_party_name}>
                                <span>{row.pickup_party_name}</span>
                            </Tooltip>
                        );
                    }

                    return (
                        <Tooltip title={row.pickup_party_name}>
                            <Link
                                component={RouterLink}
                                to={buildShipperLink(row.pickup_party_id, filters)}
                            >
                                {row.pickup_party_name}
                            </Link>
                        </Tooltip>
                    );
                },
                flex: 1,
                minWidth: 180,
            },
            {
                headerName: 'Consignee',
                field: 'delivery_party_name',
                renderCell: ({ row }): React.JSX.Element => {
                    return (
                        <Tooltip title={row.delivery_party_name}>
                            <span>{row.delivery_party_name}</span>
                        </Tooltip>
                    );
                },
                flex: 1,
                minWidth: 180,
            },
            {
                headerName: 'Origin',
                field: 'origin',
                valueGetter: (_value, row): string => {
                    return getAreaValue({
                        cityName: row.pickup_party_city_name,
                        stateCode: row.pickup_party_state_code,
                        mriAreaCode: row.pickup_party_mri_area_code,
                    });
                },
                sortComparator: (value1, value2, cellParams1, cellParams2): number => {
                    // get the display value for the current row
                    const displayOrigin1 = getAreaValue({
                        cityName: cellParams1.api.getCellValue(cellParams1.id, 'pickup_party_city_name'),
                        stateCode: cellParams1.api.getCellValue(cellParams1.id, 'pickup_party_state_code'),
                        mriAreaCode: cellParams1.api.getCellValue(cellParams1.id, 'pickup_party_mri_area_code'),
                    });
                    // get the display value for the row we're comparing to
                    const displayOrigin2 = getAreaValue({
                        cityName: cellParams2.api.getCellValue(cellParams2.id, 'pickup_party_city_name'),
                        stateCode: cellParams2.api.getCellValue(cellParams2.id, 'pickup_party_state_code'),
                        mriAreaCode: cellParams2.api.getCellValue(cellParams2.id, 'pickup_party_mri_area_code'),
                    });

                    // compare the two values to sort alphabetically
                    if (displayOrigin1 < displayOrigin2) { return -1; }
                    if (displayOrigin1 > displayOrigin2) { return 1; }
                    return 0;
                },
                renderCell: ({ row }): React.JSX.Element => {
                    const displayOrigin = getAreaValue({
                        cityName: row.pickup_party_city_name,
                        stateCode: row.pickup_party_state_code,
                        mriAreaCode: row.pickup_party_mri_area_code,
                    });

                    if (areaMatch?.params.name === row.pickup_party_mri_area_code) {
                        return (
                            <Tooltip title={displayOrigin}>
                                <span>{displayOrigin}</span>
                            </Tooltip>
                        );
                    }

                    return (
                        <Tooltip title={displayOrigin}>
                            <Link
                                component={RouterLink}
                                to={buildAreaLink(row.pickup_party_mri_area_code, filters)}
                            >
                                {displayOrigin}
                            </Link>
                        </Tooltip>
                    );
                },
                flex: 1,
                minWidth: 140,
            },
            {
                headerName: 'Destination',
                field: 'destination',
                valueGetter: (_value, row): string => {
                    return getAreaValue({
                        cityName: row.delivery_party_city_name,
                        stateCode: row.delivery_party_state_code,
                        mriAreaCode: row.delivery_party_mri_area_code,
                    });
                },
                sortComparator: (value1, value2, cellParams1, cellParams2): number => {
                    // get the display value for the current row
                    const displayDestination1 = getAreaValue({
                        cityName: cellParams1.api.getCellValue(cellParams1.id, 'delivery_party_city_name'),
                        stateCode: cellParams1.api.getCellValue(cellParams1.id, 'delivery_party_state_code'),
                        mriAreaCode: cellParams1.api.getCellValue(cellParams1.id, 'delivery_party_mri_area_code'),
                    });
                    // get the display value for the row we're comparing to
                    const displayDestination2 = getAreaValue({
                        cityName: cellParams2.api.getCellValue(cellParams2.id, 'delivery_party_city_name'),
                        stateCode: cellParams2.api.getCellValue(cellParams2.id, 'delivery_party_state_code'),
                        mriAreaCode: cellParams2.api.getCellValue(cellParams2.id, 'delivery_party_mri_area_code'),
                    });

                    // compare the two values to sort alphabetically
                    if (displayDestination1 < displayDestination2) { return -1; }
                    if (displayDestination1 > displayDestination2) { return 1; }
                    return 0;
                },
                renderCell: ({ row }): React.JSX.Element => {
                    const displayDestination = getAreaValue({
                        cityName: row.delivery_party_city_name,
                        stateCode: row.delivery_party_state_code,
                        mriAreaCode: row.delivery_party_mri_area_code,
                    });

                    if (areaMatch?.params.name === row.delivery_party_mri_area_code) {
                        return (
                            <Tooltip title={displayDestination}>
                                <span>{displayDestination}</span>
                            </Tooltip>
                        );
                    }

                    return (
                        <Tooltip title={displayDestination}>
                            <Link
                                component={RouterLink}
                                to={buildAreaLink(
                                    row.delivery_party_mri_area_code,
                                    filters
                                )}
                            >
                                {displayDestination}
                            </Link>
                        </Tooltip>
                    );
                },
                flex: 1,
                minWidth: 150,
            },
            {
                headerName: 'Total Revenue',
                field: 'total_revenue_usd_amount',
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                type: 'number',
                flex: 1,
                minWidth: 170,
            },
            {
                headerName: 'Linehaul Rate',
                field: 'linehaul_rpm',
                valueGetter: (_value, row): number | null => {
                    return row.linehaul_revenue_usd_amount !== null && row.loaded_distance_miles_amount ?
                        row.linehaul_revenue_usd_amount / row.loaded_distance_miles_amount :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                type: 'number',
                flex: 1,
                minWidth: 165,
            },
            {
                headerName: 'TLR Linehaul Rate',
                description: 'TLR Linehaul Rate',
                field: 'mri_linehaul_revenue_usd_per_mile',
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                type: 'number',
                flex: 1,
                minWidth: 165,
            },
            {
                headerName: 'LOH',
                description: 'Length of Haul',
                field: 'loaded_distance_miles_amount',
                valueFormatter: (value): string => {
                    return formatNumber(value, 0, 0);
                },
                width: 110,
                type: 'number',
            },
            {
                headerName: 'TLR LOH',
                description: 'TLR Length of Haul',
                field: 'mri_loaded_distance_miles_amount',
                valueFormatter: (value): string => {
                    return formatNumber(value, 0, 0);
                },
                width: 110,
                type: 'number',
            },
            {
                headerName: 'Yield',
                field: 'order_yield_usd',
                description: 'Network based margin per load per day',
                valueFormatter: (value): string => {
                    return formatCurrency(value, 0, 0);
                },
                width: 110,
                type: 'number',
            },
            {
                headerName: 'RPH',
                description: 'Revenue Per Hour (in USD)',
                field: 'rph',
                valueGetter: (_value, row): number | null => {
                    return row.total_revenue_usd_amount !== null && row.first_stop_departure_to_final_stop_arrival_hours_duration ?
                        row.total_revenue_usd_amount / row.first_stop_departure_to_final_stop_arrival_hours_duration :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                type: 'number',
                flex: 1,
                minWidth: 110,
            },
            {
                headerName: 'MPH',
                description: 'Miles Per Hour',
                field: 'mph',
                valueGetter: (_value, row): number | null => {
                    return row.loaded_distance_miles_amount !== null && row.first_stop_departure_to_final_stop_arrival_hours_duration ?
                        row.loaded_distance_miles_amount / row.first_stop_departure_to_final_stop_arrival_hours_duration :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                type: 'number',
                flex: 1,
                minWidth: 110,
            },
            {
                headerName: 'Empty Miles',
                field: 'empty_distance_miles_amount',
                valueFormatter: (value): string => {
                    return formatNumber(value, 0, 0);
                },
                type: 'number',
                minWidth: 160,
            },
            {
                headerName: 'Empty Mile %',
                field: 'empty_miles_pct',
                valueGetter: (_value, row): number | null => {
                    return row.empty_distance_miles_amount !== null && row.travel_distance_miles_amount
                        ? (row.empty_distance_miles_amount / row.travel_distance_miles_amount) * 100
                        : null;
                },
                valueFormatter: (value): string => {
                    return formatNumberPercent(value, 0, 0);
                },
                type: 'number',
                flex: 1,
                minWidth: 160,
            },
            {
                headerName: 'In Transit Delay',
                field: 'netwise_in_transit_delay_hours_duration',
                type: 'number',
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                flex: 1,
                minWidth: 175,
            },
            {
                headerName: 'Dwell',
                field: 'netwise_dwell_hours_duration',
                type: 'number',
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                flex: 1,
                minWidth: 150,
            },
            {
                headerName: 'Fuel Rate',
                field: 'fuel_rpm',
                valueGetter: (_value, row): number | null => {
                    return row.fuel_surcharge_usd_amount !== null && row.loaded_distance_miles_amount ?
                        row.fuel_surcharge_usd_amount / row.loaded_distance_miles_amount :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                type: 'number',
                flex: 1,
                minWidth: 135,
            },
            {
                headerName: 'TLR Fuel Rate',
                description: 'TLR Fuel Rate',
                field: 'mri_fuel_surcharge_revenue_usd_per_mile',
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                type: 'number',
                flex: 1,
                minWidth: 135,
            },
            {
                headerName: 'Accessorial Charges',
                field: 'accessorial_rpm',
                valueGetter: (_value, row): number | null => {
                    return row.accessorial_revenue_usd_amount !== null && row.loaded_distance_miles_amount ?
                        row.accessorial_revenue_usd_amount / row.loaded_distance_miles_amount :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                type: 'number',
                flex: 1,
                minWidth: 175,
            },
            {
                headerName: 'TLR Accessorial Charges',
                description: 'TLR Accessorial Charges',
                field: 'mri_accessorial_revenue_usd_per_mile',
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                type: 'number',
                flex: 1,
                minWidth: 175,
            },
            {
                headerName: 'Total RPM',
                description: 'Total Rate Per Mile',
                field: 'all_in_rate',
                valueGetter: (_value, row): number | null => {
                    return row.total_revenue_usd_amount !== null && row.loaded_distance_miles_amount ?
                        row.total_revenue_usd_amount / row.loaded_distance_miles_amount :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                type: 'number',
                flex: 1,
                minWidth: 145,
            },
            {
                headerName: 'TLR Total RPM',
                description: 'TLR Total Rate Per Mile',
                field: 'mri_total_revenue_usd_per_mile',
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                type: 'number',
                flex: 1,
                minWidth: 145,
            },
            {
                headerName: 'Commodity Code',
                field: 'commodity_code',
                type: 'string',
                flex: 1,
                minWidth: 190,
            },
            {
                headerName: 'Equipment Type',
                field: 'freight_order_specified_eqpt_code',
                type: 'string',
                flex: 1,
                minWidth: 190,
            },
            {
                headerName: 'Source System',
                field: 'reporting_source_code',
                valueFormatter: (value: TmsSources | null): string => {
                    if (!value) {
                        return ValueReferences.NA;
                    }

                    if (value === TmsSources.TMW) {
                        return 'TMW Suite';
                    }

                    return value;
                },
                type: 'string',
                flex: 1,
                minWidth: 190,
            },
            {
                headerName: 'TLR Update Date',
                field: 'mri_rate_date',
                valueFormatter: (value): string => {
                    if (value === '') {
                        return ValueReferences.NA;
                    }

                    return apiDateToLocalDate(value).toLocaleDateString();
                },
                type: 'string',
                flex: 1,
                minWidth: 190,
            },
        ];
    }, [
        areaMatch?.params,
        shipperMatch?.params,
        filters,
    ]);

    const handleSelectedEquipmentTypeChange = (event: SelectChangeEvent): void => {
        handleEquipmentTypeChange(event.target.value as EquipmentTypeCode);
    };

    const columnSearchPredicate = (column: GridColDef, searchValue: string): boolean => {
        return (
            (column.headerName || column.field).toLowerCase().indexOf(searchValue) > -1 ||
            (column.description || '').toLowerCase().indexOf(searchValue) > -1
        );
    };

    return (
        <StyledDiv className={classes.root}>
            <Grid
                container
                spacing={2}
                sx={{
                    alignItems: 'flex-end',
                }}
            >
                <Grid item>
                    <Typography variant='h4'>Orders</Typography>
                </Grid>
                <Grid item>
                    <span className={classes.orderCountBadge}>
                        {isLoading ? <Skeleton width='10px' /> : formatNumber(orderCount)}
                    </span>
                </Grid>
                <Grid item>
                    <FormControl sx={{ minWidth: 160 }}>
                        <InputLabel id='equipmentType-label'>TLR Equipment Type</InputLabel>
                        <Select
                            labelId='equipmentType-label'
                            variant='outlined'
                            size='small'
                            value={equipmentType}
                            label='TLR Equipment Type'
                            onChange={handleSelectedEquipmentTypeChange}
                        >
                            <MenuItem value={EquipmentTypeCode.VN}>Dry Van</MenuItem>
                            <MenuItem value={EquipmentTypeCode.FB}>Flatbed</MenuItem>
                            <MenuItem value={EquipmentTypeCode.RF}>Refrigerated</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            <div className={classes.tableContents}>
                <DataGridPremium
                    rows={orders}
                    // rowCount={orderCount}
                    columns={columns}
                    loading={isLoading}
                    density='compact'
                    disableRowSelectionOnClick
                    disableRowGrouping
                    disableAggregation
                    getRowId={(row): string => {
                        return `${row.freight_order_number}${row.reporting_source_code}`;
                    }}
                    paginationModel={{
                        page,
                        pageSize,
                    }}
                    onPaginationModelChange={(model: GridPaginationModel): void => {
                        setPage(model.page);
                        setPageSize(model.pageSize);
                    }}
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    pagination
                    initialState={{
                        density: 'compact',
                    }}
                    slots={{
                        toolbar: CustomToolbar,
                    }}
                    slotProps={{
                        columnsManagement: {
                            searchPredicate: columnSearchPredicate,
                        },
                    }}
                />
            </div>
        </StyledDiv>
    );
};

export default OrderTable;
